import type { ComponentCompileResult, HitShape } from "./component-registry";
import { ComponentRegistry, type ComponentRenderTarget } from "./component-registry";
import {
  parseSynthDocument,
  type Artboard,
  type ControlEntity,
  type JsonObject,
  type JsonValue,
  type Point,
  type PortEntity,
  type SceneNode,
  type SignalConnection,
  type SignalNodeEntity,
  type SynthDocument,
} from "./document";
import {
  frameMatrix,
  IDENTITY_MATRIX,
  invertMatrix,
  localFrameBounds,
  multiplyMatrices,
  transformPoint,
  transformRect,
  translationMatrix,
  unionRects,
  type Matrix2D,
  type Rect,
} from "./geometry";
import { textGraphicContentBounds } from "./text-layout";
import type {
  GraphicFill,
  GraphicGroup,
  GraphicNode,
  GraphicStroke,
  GraphicTree,
} from "./graphic-tree";
import { SpatialHitIndex, type CompiledHitEntry } from "./spatial-index";

export type DiagnosticSeverity = "error" | "warning";

export type SynthDiagnostic = {
  severity: DiagnosticSeverity;
  code: string;
  message: string;
  nodeId?: string;
  artboardId?: string;
  entityId?: string;
  styleId?: string;
};

export type CompiledNode = {
  id: string;
  source: SceneNode;
  parentId: string | null;
  childIds: readonly string[];
  artboardId: string | null;
  localTransform: Matrix2D;
  worldTransform: Matrix2D;
  localLayoutBounds: Rect;
  layoutBounds: Rect;
  localVisualBounds: Rect;
  visualBounds: Rect;
  localAnchors: Readonly<Record<string, Point>>;
  anchors: Readonly<Record<string, Point>>;
  resolvedStyle: Readonly<JsonObject>;
  graphic: GraphicGroup;
  visible: boolean;
  locked: boolean;
  zIndex: number;
};

export type CompiledArtboard = {
  id: string;
  source: Artboard;
  rootIds: readonly string[];
  bounds: Rect;
};

export type CompiledScene = {
  artboards: ReadonlyMap<string, CompiledArtboard>;
  roots: readonly string[];
  parentIndex: ReadonlyMap<string, string | null>;
};

export type CompiledSemanticEntity<T> = {
  entity: T;
  nodeIds: readonly string[];
};

export type CompiledSemantics = {
  controls: ReadonlyMap<string, CompiledSemanticEntity<ControlEntity>>;
  ports: ReadonlyMap<string, CompiledSemanticEntity<PortEntity>>;
  signalNodes: ReadonlyMap<string, CompiledSemanticEntity<SignalNodeEntity>>;
  connections: readonly SignalConnection[];
};

export type CompiledSynth = {
  document: SynthDocument;
  scene: CompiledScene;
  semantics: CompiledSemantics;
  resolvedTokens: Readonly<Record<string, JsonValue>>;
  renderPlan: GraphicTree;
  nodeIndex: ReadonlyMap<string, CompiledNode>;
  hitIndex: SpatialHitIndex;
  portAnchors: ReadonlyMap<string, Point>;
  signalNodeAnchors: ReadonlyMap<string, Point>;
  diagnostics: readonly SynthDiagnostic[];
  valid: boolean;
  contentHash: string;
};

export type CompileSynthOptions = {
  registry?: ComponentRegistry;
  target?: ComponentRenderTarget;
  hitCellSize?: number;
};

type ResolvedStyles = {
  styles: ReadonlyMap<string, JsonObject>;
  tokens: Readonly<Record<string, JsonValue>>;
  resolveValue: (value: JsonValue) => JsonValue;
};

type LocalVisual = {
  graphics: GraphicNode[];
  layoutBounds: Rect;
  visualBounds: Rect;
  anchors: Record<string, Point>;
  hitShape: HitShape;
};

function sortedEntries<T>(record: Readonly<Record<string, T>>) {
  return Object.entries(record).sort(([first], [second]) => first.localeCompare(second));
}

function addDiagnostic(diagnostics: SynthDiagnostic[], diagnostic: SynthDiagnostic) {
  diagnostics.push(diagnostic);
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return "[" + value.map(stableSerialize).join(",") + "]";
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([first], [second]) => first.localeCompare(second));
  return (
    "{" +
    entries.map(([key, item]) => JSON.stringify(key) + ":" + stableSerialize(item)).join(",") +
    "}"
  );
}

export function synthDocumentContentHash(document: SynthDocument) {
  const source = stableSerialize(document);
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= BigInt(source.charCodeAt(index));
    hash = (hash * prime) & mask;
  }
  return "fnv1a64:" + hash.toString(16).padStart(16, "0");
}

function validateRecordIds<T extends { id: string }>(
  record: Readonly<Record<string, T>>,
  kind: string,
  diagnostics: SynthDiagnostic[],
) {
  for (const [key, value] of sortedEntries(record)) {
    if (key !== value.id) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "record-id-mismatch",
        message: kind + " key '" + key + "' does not match its id '" + value.id + "'.",
        entityId: value.id,
      });
    }
  }
}

function resolveStyles(document: SynthDocument, diagnostics: SynthDiagnostic[]): ResolvedStyles {
  const resolvedTokens: Record<string, JsonValue> = {};
  const resolvingTokens = new Set<string>();

  const resolveValue = (value: JsonValue): JsonValue => {
    if (Array.isArray(value)) return value.map(resolveValue);
    if (value === null || typeof value !== "object") return value;
    const keys = Object.keys(value);
    if (keys.length === 1 && keys[0] === "$token") {
      const tokenName = value.$token;
      if (typeof tokenName === "string") return resolveToken(tokenName);
    }
    return Object.fromEntries(
      Object.entries(value)
        .sort(([first], [second]) => first.localeCompare(second))
        .map(([key, item]) => [key, resolveValue(item)]),
    );
  };

  const resolveToken = (name: string): JsonValue => {
    if (Object.hasOwn(resolvedTokens, name)) return resolvedTokens[name]!;
    const token = document.styles.tokens[name];
    if (!token) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-style-token",
        message: "Style token '" + name + "' does not exist.",
        styleId: name,
      });
      return null;
    }
    if (resolvingTokens.has(name)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "style-token-cycle",
        message: "Style token '" + name + "' has a circular reference.",
        styleId: name,
      });
      return null;
    }
    resolvingTokens.add(name);
    const resolved = resolveValue(token.value);
    resolvingTokens.delete(name);
    resolvedTokens[name] = resolved;
    return resolved;
  };

  for (const [name] of sortedEntries(document.styles.tokens)) resolveToken(name);

  const resolvedStyles = new Map<string, JsonObject>();
  const resolvingStyles = new Set<string>();
  const resolveStyle = (name: string): JsonObject => {
    const cached = resolvedStyles.get(name);
    if (cached) return cached;
    const style = document.styles.definitions[name];
    if (!style) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-style",
        message: "Named style '" + name + "' does not exist.",
        styleId: name,
      });
      return {};
    }
    if (resolvingStyles.has(name)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "style-cycle",
        message: "Named style '" + name + "' has a circular extends chain.",
        styleId: name,
      });
      return {};
    }
    resolvingStyles.add(name);
    const parent = style.extends ? resolveStyle(style.extends) : {};
    const resolved = {
      ...parent,
      ...Object.fromEntries(
        Object.entries(style.properties).map(([key, value]) => [key, resolveValue(value)]),
      ),
    };
    resolvingStyles.delete(name);
    resolvedStyles.set(name, resolved);
    return resolved;
  };

  for (const [name] of sortedEntries(document.styles.definitions)) resolveStyle(name);
  return { styles: resolvedStyles, tokens: resolvedTokens, resolveValue };
}

function styleString(style: Readonly<JsonObject>, key: string, fallback: string) {
  const value = style[key];
  return typeof value === "string" ? value : fallback;
}

function styleNumber(style: Readonly<JsonObject>, key: string, fallback: number) {
  const value = style[key];
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function textLetterSpacing(style: Readonly<JsonObject>, fontSize: number) {
  const relative = style.trackingEm;
  return typeof relative === "number" && Number.isFinite(relative)
    ? relative * fontSize
    : styleNumber(style, "letterSpacing", 0);
}

function textLineHeight(style: Readonly<JsonObject>, fontSize: number) {
  if (style.lineHeightMode === "auto") return fontSize * 1.08;
  const ratio = style.lineHeightRatio;
  if (typeof ratio === "number" && Number.isFinite(ratio)) {
    return Math.max(0.5, ratio) * fontSize;
  }
  return styleNumber(style, "lineHeight", fontSize * 1.08);
}

function solidFill(style: Readonly<JsonObject>): GraphicFill | undefined {
  const colour = style.fill;
  if (typeof colour !== "string" || colour === "none") return undefined;
  return {
    kind: "solid",
    colour,
    alpha: styleNumber(style, "fillAlpha", 1),
  };
}

function graphicStroke(style: Readonly<JsonObject>): GraphicStroke | undefined {
  const colour = style.stroke;
  if (typeof colour !== "string" || colour === "none") return undefined;
  return {
    colour,
    width: styleNumber(style, "strokeWidth", 0.4),
    alpha: styleNumber(style, "strokeAlpha", 1),
  };
}

function standardAnchors(bounds: Rect) {
  return {
    primary: { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
    center: { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
    left: { x: bounds.x, y: bounds.y + bounds.height / 2 },
    right: { x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
    top: { x: bounds.x + bounds.width / 2, y: bounds.y },
    bottom: { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
  };
}

function panelSymbolGraphics(
  symbol: NonNullable<SceneNode["symbol"]>,
  width: number,
  height: number,
  style: Readonly<JsonObject>,
): GraphicNode[] {
  const arrowCaption =
    symbol.type === "signal-flow" ? ([symbol.from, symbol.to] as const) : undefined;
  const value =
    symbol.type === "waveform"
      ? {
          sine: "∿",
          saw: "⌁",
          ramp: "⌁",
          square: "⊓",
          triangle: "△",
          "narrow-pulse": "⊓|",
        }[symbol.waveform]
      : "";
  const horizontalScale = styleNumber(style, "horizontalScale", 1);
  const scaledWidth = width * horizontalScale;
  const left = (width - scaledWidth) / 2;
  const x = (ratio: number) => left + scaledWidth * ratio;
  const y = (ratio: number) => height * ratio;
  const colour = styleString(style, "colour", "#e8ece9");
  const symbolStroke: GraphicStroke = {
    colour,
    width: Math.max(0.2, Math.min(0.34, height * 0.11)),
    cap: "round",
    join: "round",
  };
  if (arrowCaption) {
    const fontFamily = styleString(style, "fontFamily", "Arial Narrow, Arial, sans-serif");
    const fontSize = styleNumber(style, "fontSize", Math.min(4, height));
    const fontWeight = styleNumber(style, "fontWeight", 700);
    const letterSpacing = textLetterSpacing(style, fontSize);
    const lineHeight = textLineHeight(style, fontSize);
    const number = (text: string, centre: number): GraphicNode => ({
      type: "text",
      value: text,
      x: x(centre),
      y: 0,
      align: "center",
      verticalAlign: "top",
      frameMode: "hug",
      overflow: "visible",
      colour,
      fontFamily,
      fontSize,
      fontWeight,
      letterSpacing,
      lineHeight,
      horizontalScale,
    });
    const arrowY = y(0.43);
    return [
      number(arrowCaption[0], 0.17),
      {
        type: "line",
        from: { x: x(0.36), y: arrowY },
        to: { x: x(0.65), y: arrowY },
        stroke: symbolStroke,
      },
      {
        type: "polyline",
        points: [
          { x: x(0.57), y: y(0.28) },
          { x: x(0.65), y: arrowY },
          { x: x(0.57), y: y(0.58) },
        ],
        stroke: symbolStroke,
      },
      number(arrowCaption[1], 0.84),
    ];
  }
  const path =
    value === "∿"
      ? `M ${x(0.08)} ${y(0.52)}
         C ${x(0.18)} ${y(0.14)}, ${x(0.28)} ${y(0.14)}, ${x(0.38)} ${y(0.52)}
         C ${x(0.48)} ${y(0.9)}, ${x(0.58)} ${y(0.9)}, ${x(0.68)} ${y(0.52)}
         C ${x(0.78)} ${y(0.14)}, ${x(0.86)} ${y(0.14)}, ${x(0.94)} ${y(0.52)}`
      : value === "⌁"
        ? `M ${x(0.08)} ${y(0.8)} L ${x(0.47)} ${y(0.2)} L ${x(0.47)} ${y(
            0.8,
          )} L ${x(0.92)} ${y(0.2)}`
        : value === "△"
          ? `M ${x(0.08)} ${y(0.8)} L ${x(0.5)} ${y(0.18)} L ${x(0.92)} ${y(0.8)}`
          : value === "⊓|"
            ? `M ${x(0.08)} ${y(0.8)} L ${x(0.08)} ${y(0.2)} L ${x(0.46)} ${y(
                0.2,
              )} L ${x(0.46)} ${y(0.8)} L ${x(0.92)} ${y(0.8)}`
            : `M ${x(0.08)} ${y(0.8)} L ${x(0.08)} ${y(0.2)} L ${x(0.92)} ${y(
                0.2,
              )} L ${x(0.92)} ${y(0.8)}`;
  return [{ type: "path", data: path, stroke: symbolStroke }];
}

function directNodeGraphics(
  node: SceneNode,
  style: Readonly<JsonObject>,
  document: SynthDocument,
  diagnostics: SynthDiagnostic[],
): GraphicNode[] {
  const { width, height } = node.frame;
  const fill = solidFill(style);
  const stroke = graphicStroke(style);

  if (node.kind === "surface") {
    return [
      {
        type: "rect",
        x: 0,
        y: 0,
        width,
        height,
        radius: styleNumber(style, "radius", 0),
        fill,
        stroke,
      },
    ];
  }
  if (node.kind === "text" && node.text) {
    return [
      {
        type: "text",
        value: node.text.value,
        x: node.text.align === "left" ? 0 : node.text.align === "right" ? width : width / 2,
        y:
          node.text.verticalAlign === "top"
            ? 0
            : node.text.verticalAlign === "bottom"
              ? height
              : height / 2,
        width,
        height,
        align: node.text.align,
        verticalAlign: node.text.verticalAlign,
        wrap: node.text.wrap,
        frameMode: node.text.frameMode,
        overflow: node.text.overflow,
        colour: styleString(style, "colour", "#e8ece9"),
        fontFamily: styleString(style, "fontFamily", "Arial Narrow, Arial, sans-serif"),
        fontSize: styleNumber(style, "fontSize", Math.min(4, height)),
        fontWeight: styleNumber(style, "fontWeight", 700),
        letterSpacing: textLetterSpacing(
          style,
          styleNumber(style, "fontSize", Math.min(4, height)),
        ),
        lineHeight: textLineHeight(style, styleNumber(style, "fontSize", Math.min(4, height))),
        horizontalScale: styleNumber(style, "horizontalScale", 1),
        stroke,
      },
    ];
  }
  if (node.kind === "symbol" && node.symbol) {
    return panelSymbolGraphics(node.symbol, width, height, style);
  }
  if (node.kind === "shape" && node.shape) {
    if (node.shape.type === "rect") {
      return [
        {
          type: "rect",
          x: 0,
          y: 0,
          width,
          height,
          radius: node.shape.radius,
          fill,
          stroke,
        },
      ];
    }
    if (node.shape.type === "ellipse") {
      return [
        {
          type: "ellipse",
          cx: width / 2,
          cy: height / 2,
          radiusX: width / 2,
          radiusY: height / 2,
          fill,
          stroke,
        },
      ];
    }
    if (node.shape.type === "line") {
      return [
        {
          type: "line",
          from: node.shape.from,
          to: node.shape.to,
          stroke: stroke ?? { colour: "#e8ece9", width: 0.4 },
        },
      ];
    }
    if (node.shape.type === "polyline") {
      return [
        {
          type: "polyline",
          points: node.shape.points,
          closed: node.shape.closed,
          fill,
          stroke,
        },
      ];
    }
    return [{ type: "path", data: node.shape.data, fill, stroke }];
  }
  if (node.kind === "image" && node.image) {
    if (!document.assets[node.image.assetId]) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-asset",
        message: "Image asset '" + node.image.assetId + "' does not exist.",
        nodeId: node.id,
      });
      return [];
    }
    return [
      {
        type: "image",
        assetId: node.image.assetId,
        x: 0,
        y: 0,
        width,
        height,
        fit: node.image.fit,
      },
    ];
  }
  return [];
}

function compileLocalVisual(
  node: SceneNode,
  document: SynthDocument,
  style: JsonObject,
  styles: ResolvedStyles,
  registry: ComponentRegistry,
  target: ComponentRenderTarget,
  diagnostics: SynthDiagnostic[],
): LocalVisual {
  const frameBounds = localFrameBounds(node.frame);
  const graphics = directNodeGraphics(node, style, document, diagnostics);
  const defaults: LocalVisual = {
    graphics,
    layoutBounds: frameBounds,
    visualBounds: frameBounds,
    anchors: standardAnchors(frameBounds),
    hitShape: { type: "rect", rect: frameBounds },
  };

  if (node.kind === "text" && node.text && graphics.length === 1 && graphics[0]?.type === "text") {
    const contentBounds = textGraphicContentBounds(graphics[0]);
    if (node.text.frameMode === "hug") {
      return {
        graphics,
        layoutBounds: contentBounds,
        visualBounds: contentBounds,
        anchors: standardAnchors(contentBounds),
        hitShape: { type: "rect", rect: contentBounds },
      };
    }
    if (node.text.overflow === "visible") {
      return {
        ...defaults,
        visualBounds: unionRects([frameBounds, contentBounds]) ?? frameBounds,
      };
    }
  }

  if (node.kind !== "component" || !node.component) return defaults;
  const definition = registry.get(node.component.type);
  if (!definition) {
    addDiagnostic(diagnostics, {
      severity: "error",
      code: "missing-component",
      message: "Component '" + node.component.type + "' is not registered.",
      nodeId: node.id,
    });
    return { ...defaults, graphics: [] };
  }

  const parsedProps = definition.propertySchema.safeParse(node.component.props);
  if (!parsedProps.success) {
    addDiagnostic(diagnostics, {
      severity: "error",
      code: "invalid-component-properties",
      message:
        "Component '" +
        node.component.type +
        "' has invalid properties: " +
        parsedProps.error.issues.map((issue) => issue.message).join("; "),
      nodeId: node.id,
    });
    return { ...defaults, graphics: [] };
  }

  let result: ComponentCompileResult;
  try {
    result = definition.compile({
      node,
      frame: frameBounds,
      props: parsedProps.data,
      variant: node.component.variant,
      style,
      tokens: styles.tokens,
      target,
    });
  } catch (error) {
    addDiagnostic(diagnostics, {
      severity: "error",
      code: "component-compile-failed",
      message:
        "Component '" +
        node.component.type +
        "' failed to compile: " +
        (error instanceof Error ? error.message : String(error)),
      nodeId: node.id,
    });
    return { ...defaults, graphics: [] };
  }

  for (const diagnostic of result.diagnostics ?? []) {
    addDiagnostic(diagnostics, { ...diagnostic, nodeId: node.id });
  }
  const layoutBounds = result.layoutBounds ?? frameBounds;
  return {
    graphics: result.graphics,
    layoutBounds,
    visualBounds: result.visualBounds ?? layoutBounds,
    anchors: { ...standardAnchors(layoutBounds), ...result.anchors },
    hitShape: result.hitShape ?? { type: "rect", rect: layoutBounds },
  };
}

function pointForBinding(node: CompiledNode, anchorName: string | undefined) {
  return node.anchors[anchorName ?? "primary"];
}

function createSemanticMap<T extends { id: string }>(
  record: Readonly<Record<string, T>>,
  bindings: ReadonlyMap<string, string[]>,
) {
  return new Map(
    sortedEntries(record).map(([id, entity]) => [id, { entity, nodeIds: bindings.get(id) ?? [] }]),
  );
}

export function compileSynthDocument(
  value: unknown,
  options: CompileSynthOptions = {},
): CompiledSynth {
  const document = parseSynthDocument(value);
  const diagnostics: SynthDiagnostic[] = [];
  const registry = options.registry ?? new ComponentRegistry();
  const target = options.target ?? "screen";
  validateRecordIds(document.artboards, "Artboard", diagnostics);
  validateRecordIds(document.nodes, "Node", diagnostics);
  validateRecordIds(document.semantics.controls, "Control", diagnostics);
  validateRecordIds(document.semantics.ports, "Port", diagnostics);
  validateRecordIds(document.semantics.signalNodes, "Signal node", diagnostics);
  validateRecordIds(document.assets, "Asset", diagnostics);
  const styles = resolveStyles(document, diagnostics);

  const rootSet = new Set<string>();
  for (const rootId of document.roots) {
    if (rootSet.has(rootId)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "duplicate-root",
        message: "Root node '" + rootId + "' appears more than once.",
        nodeId: rootId,
      });
    }
    rootSet.add(rootId);
    if (!document.nodes[rootId]) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-root",
        message: "Root node '" + rootId + "' does not exist.",
        nodeId: rootId,
      });
    }
  }

  const parentIndex = new Map<string, string | null>();
  for (const rootId of rootSet) parentIndex.set(rootId, null);
  for (const [parentId, parent] of sortedEntries(document.nodes)) {
    const seenChildren = new Set<string>();
    for (const childId of parent.children) {
      if (seenChildren.has(childId)) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "duplicate-child",
          message: "Node '" + parentId + "' contains child '" + childId + "' more than once.",
          nodeId: parentId,
        });
        continue;
      }
      seenChildren.add(childId);
      if (!document.nodes[childId]) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "missing-child",
          message: "Node '" + parentId + "' references missing child '" + childId + "'.",
          nodeId: parentId,
        });
        continue;
      }
      if (rootSet.has(childId)) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "root-has-parent",
          message: "Root node '" + childId + "' cannot also be a child of '" + parentId + "'.",
          nodeId: childId,
        });
        continue;
      }
      const existingParent = parentIndex.get(childId);
      if (existingParent !== undefined && existingParent !== parentId) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "multiple-parents",
          message:
            "Node '" +
            childId +
            "' is owned by both '" +
            existingParent +
            "' and '" +
            parentId +
            "'.",
          nodeId: childId,
        });
        continue;
      }
      parentIndex.set(childId, parentId);
    }
  }

  for (const [nodeId, node] of sortedEntries(document.nodes)) {
    if (rootSet.has(nodeId)) {
      if (!node.artboardId) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "root-missing-artboard",
          message: "Root node '" + nodeId + "' must identify its artboard.",
          nodeId,
        });
      } else if (!document.artboards[node.artboardId]) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "missing-artboard",
          message:
            "Root node '" + nodeId + "' references missing artboard '" + node.artboardId + "'.",
          nodeId,
          artboardId: node.artboardId,
        });
      }
    } else if (node.artboardId) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "nested-artboard",
        message: "Only root nodes may identify an artboard.",
        nodeId,
        artboardId: node.artboardId,
      });
    }
  }

  const nodeIndex = new Map<string, CompiledNode>();
  const hitEntries: CompiledHitEntry[] = [];
  const visiting = new Set<string>();
  let nextZIndex = 0;

  const compileBranch = (
    nodeId: string,
    parentWorld: Matrix2D,
    artboardId: string | null,
    inheritedVisible: boolean,
    inheritedLocked: boolean,
  ): GraphicGroup | null => {
    const cached = nodeIndex.get(nodeId);
    if (cached) return cached.graphic;
    const node = document.nodes[nodeId];
    if (!node) return null;
    if (visiting.has(nodeId)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "hierarchy-cycle",
        message: "Scene hierarchy contains a cycle at node '" + nodeId + "'.",
        nodeId,
      });
      return null;
    }
    visiting.add(nodeId);
    const zIndex = nextZIndex;
    nextZIndex += 1;
    const localTransform = frameMatrix(node.frame, node.rotation, node.anchor);
    const worldTransform = multiplyMatrices(parentWorld, localTransform);
    const namedStyle = node.styleRef ? styles.styles.get(node.styleRef) : undefined;
    if (node.styleRef && !namedStyle) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-node-style",
        message: "Node '" + nodeId + "' references missing style '" + node.styleRef + "'.",
        nodeId,
        styleId: node.styleRef,
      });
    }
    const resolvedOverrides = node.styleOverrides
      ? (Object.fromEntries(
          Object.entries(node.styleOverrides).map(([key, item]) => [
            key,
            styles.resolveValue(item),
          ]),
        ) as JsonObject)
      : {};
    const resolvedStyle = { ...(namedStyle ?? {}), ...resolvedOverrides };
    const localVisual = compileLocalVisual(
      node,
      document,
      resolvedStyle,
      styles,
      registry,
      target,
      diagnostics,
    );
    const childGraphics: GraphicNode[] = [];
    const compiledChildIds: string[] = [];
    for (const childId of node.children) {
      if (parentIndex.get(childId) !== nodeId) continue;
      const childGraphic = compileBranch(
        childId,
        worldTransform,
        artboardId,
        inheritedVisible && node.visible,
        inheritedLocked || node.locked,
      );
      if (childGraphic) {
        childGraphics.push(childGraphic);
        compiledChildIds.push(childId);
      }
    }
    const populatedGroup = node.kind === "group" && compiledChildIds.length > 0;
    const childLocalVisualBounds = compiledChildIds
      .map((childId) => nodeIndex.get(childId))
      .filter((child): child is CompiledNode => child !== undefined)
      .map((child) => transformRect(child.localTransform, child.localVisualBounds));
    const localVisualBounds = populatedGroup
      ? (unionRects(childLocalVisualBounds) ?? localVisual.visualBounds)
      : localVisual.visualBounds;
    const ownVisualBounds = transformRect(worldTransform, localVisualBounds);
    const childBounds = compiledChildIds
      .map((childId) => nodeIndex.get(childId)?.visualBounds)
      .filter((bounds): bounds is Rect => bounds !== undefined);
    const visualBounds = populatedGroup
      ? (unionRects(childBounds) ?? ownVisualBounds)
      : (unionRects([ownVisualBounds, ...childBounds]) ?? ownVisualBounds);
    const layoutBounds = transformRect(worldTransform, localVisual.layoutBounds);
    const anchors = Object.fromEntries(
      Object.entries(localVisual.anchors).map(([name, point]) => [
        name,
        transformPoint(worldTransform, point),
      ]),
    );
    const visible = inheritedVisible && node.visible;
    const locked = inheritedLocked || node.locked;
    const authoredHitBounds = transformRect(worldTransform, localVisual.layoutBounds);
    const preservesAuthoredGroupHitArea = populatedGroup && node.selectionRole === "slot";
    const graphic: GraphicGroup = {
      type: "group",
      id: nodeId,
      className:
        node.binding?.kind === "control"
          ? "wire-control semantic-control"
          : node.binding?.kind === "port"
            ? "wire-port semantic-port"
            : node.binding?.kind === "signal-node"
              ? "semantic-signal-node"
              : undefined,
      transform: localTransform,
      visible: node.visible,
      alpha: styleNumber(resolvedStyle, "alpha", 1),
      children: [...localVisual.graphics, ...childGraphics],
    };
    const compiled: CompiledNode = {
      id: nodeId,
      source: node,
      parentId: parentIndex.get(nodeId) ?? null,
      childIds: compiledChildIds,
      artboardId,
      localTransform,
      worldTransform,
      localLayoutBounds: localVisual.layoutBounds,
      layoutBounds,
      localVisualBounds,
      visualBounds,
      localAnchors: localVisual.anchors,
      anchors,
      resolvedStyle,
      graphic,
      visible,
      locked,
      zIndex,
    };
    nodeIndex.set(nodeId, compiled);
    visiting.delete(nodeId);

    const inverseWorldTransform = invertMatrix(worldTransform);
    if (visible && inverseWorldTransform) {
      hitEntries.push({
        nodeId,
        artboardId,
        bounds: preservesAuthoredGroupHitArea
          ? (unionRects([visualBounds, authoredHitBounds]) ?? visualBounds)
          : visualBounds,
        hitShape:
          populatedGroup && !preservesAuthoredGroupHitArea
            ? { type: "rect", rect: localVisualBounds }
            : localVisual.hitShape,
        inverseWorldTransform,
        locked,
        selectionRole: node.selectionRole,
        zIndex,
      });
    }
    return graphic;
  };

  const rootsByArtboard = new Map<string, string[]>();
  for (const rootId of rootSet) {
    const root = document.nodes[rootId];
    if (!root?.artboardId || !document.artboards[root.artboardId]) continue;
    const roots = rootsByArtboard.get(root.artboardId) ?? [];
    roots.push(rootId);
    rootsByArtboard.set(root.artboardId, roots);
  }
  const artboardOrder = [
    ...rootsByArtboard.keys(),
    ...Object.keys(document.artboards)
      .sort()
      .filter((id) => !rootsByArtboard.has(id)),
  ];
  const compiledArtboards = new Map<string, CompiledArtboard>();
  const artboardGraphics: GraphicNode[] = [];
  for (const artboardId of artboardOrder) {
    const artboard = document.artboards[artboardId]!;
    const artboardRootIds = rootsByArtboard.get(artboardId) ?? [];
    const translation = translationMatrix(artboard.frame.x, artboard.frame.y);
    const roots = artboardRootIds
      .map((rootId) =>
        compileBranch(rootId, translation, artboardId, artboard.visible, artboard.locked),
      )
      .filter((graphic): graphic is GraphicGroup => graphic !== null);
    artboardGraphics.push({
      type: "group",
      id: "artboard:" + artboardId,
      transform: translation,
      visible: artboard.visible,
      children: roots,
    });
    compiledArtboards.set(artboardId, {
      id: artboardId,
      source: artboard,
      rootIds: artboardRootIds,
      bounds: artboard.frame,
    });
  }

  const orphanGraphics: GraphicNode[] = [];
  for (const [nodeId] of sortedEntries(document.nodes)) {
    if (nodeIndex.has(nodeId)) continue;
    addDiagnostic(diagnostics, {
      severity: "warning",
      code: "orphan-node",
      message: "Node '" + nodeId + "' is not reachable from an artboard root.",
      nodeId,
    });
    const graphic = compileBranch(nodeId, IDENTITY_MATRIX, null, false, false);
    if (graphic) orphanGraphics.push(graphic);
  }
  if (orphanGraphics.length > 0) {
    artboardGraphics.push({
      type: "group",
      id: "orphans",
      visible: false,
      children: orphanGraphics,
    });
  }

  const controlBindings = new Map<string, string[]>();
  const portBindings = new Map<string, string[]>();
  const signalNodeBindings = new Map<string, string[]>();
  const portAnchors = new Map<string, Point>();
  const signalNodeAnchors = new Map<string, Point>();
  const entityRecords = {
    control: document.semantics.controls,
    port: document.semantics.ports,
    "signal-node": document.semantics.signalNodes,
  } as const;
  const bindingMaps = {
    control: controlBindings,
    port: portBindings,
    "signal-node": signalNodeBindings,
  } as const;

  for (const [nodeId, node] of sortedEntries(document.nodes)) {
    if (!node.binding) continue;
    const entity = entityRecords[node.binding.kind][node.binding.entityId];
    if (!entity) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-binding-entity",
        message:
          "Node '" +
          nodeId +
          "' binds to missing " +
          node.binding.kind +
          " '" +
          node.binding.entityId +
          "'.",
        nodeId,
        entityId: node.binding.entityId,
      });
      continue;
    }
    const bindings = bindingMaps[node.binding.kind];
    bindings.set(node.binding.entityId, [...(bindings.get(node.binding.entityId) ?? []), nodeId]);
    const compiled = nodeIndex.get(nodeId);
    if (!compiled || node.binding.kind === "control") continue;
    const anchor = pointForBinding(compiled, node.binding.anchor);
    if (!anchor) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-binding-anchor",
        message:
          "Node '" +
          nodeId +
          "' does not provide anchor '" +
          (node.binding.anchor ?? "primary") +
          "'.",
        nodeId,
        entityId: node.binding.entityId,
      });
      continue;
    }
    const anchorMap = node.binding.kind === "port" ? portAnchors : signalNodeAnchors;
    if (anchorMap.has(node.binding.entityId)) {
      addDiagnostic(diagnostics, {
        severity: "warning",
        code: "duplicate-semantic-anchor",
        message:
          "Semantic entity '" +
          node.binding.entityId +
          "' has more than one visual anchor; the first is used.",
        nodeId,
        entityId: node.binding.entityId,
      });
    } else {
      anchorMap.set(node.binding.entityId, anchor);
    }
  }

  for (const [portId] of sortedEntries(document.semantics.ports)) {
    if (!portAnchors.has(portId)) {
      addDiagnostic(diagnostics, {
        severity: "warning",
        code: "inaccessible-port",
        message: "Port '" + portId + "' has no compiled visual anchor.",
        entityId: portId,
      });
    }
  }
  for (const [portId, port] of sortedEntries(document.semantics.ports)) {
    if (port.normalledFrom && !document.semantics.ports[port.normalledFrom]) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-normalled-port",
        message:
          "Port '" + portId + "' references missing normalled source '" + port.normalledFrom + "'.",
        entityId: portId,
      });
    }
  }

  const signalEndpointIds = new Set([
    ...Object.keys(document.semantics.ports),
    ...Object.keys(document.semantics.signalNodes),
  ]);
  const connectionIds = new Set<string>();
  for (const connection of document.semantics.connections) {
    if (connectionIds.has(connection.id)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "duplicate-connection",
        message: "Signal connection '" + connection.id + "' appears more than once.",
        entityId: connection.id,
      });
    }
    connectionIds.add(connection.id);
    if (!signalEndpointIds.has(connection.fromId)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-connection-source",
        message:
          "Signal connection '" +
          connection.id +
          "' references missing source '" +
          connection.fromId +
          "'.",
        entityId: connection.id,
      });
    }
    if (!signalEndpointIds.has(connection.toId)) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "missing-connection-destination",
        message:
          "Signal connection '" +
          connection.id +
          "' references missing destination '" +
          connection.toId +
          "'.",
        entityId: connection.id,
      });
    }
    if (connection.fromId === connection.toId) {
      addDiagnostic(diagnostics, {
        severity: "error",
        code: "self-connection",
        message: "Signal connection '" + connection.id + "' connects an entity to itself.",
        entityId: connection.id,
      });
    }
    if (connection.activeWhen) {
      const control = document.semantics.controls[connection.activeWhen.controlId];
      if (!control) {
        addDiagnostic(diagnostics, {
          severity: "error",
          code: "missing-condition-control",
          message:
            "Signal connection '" +
            connection.id +
            "' references missing condition control '" +
            connection.activeWhen.controlId +
            "'.",
          entityId: connection.id,
        });
      } else {
        const optionIds = new Set(control.options?.map(({ id }) => id) ?? []);
        for (const optionId of connection.activeWhen.optionIds) {
          if (!optionIds.has(optionId)) {
            addDiagnostic(diagnostics, {
              severity: "error",
              code: "missing-condition-option",
              message:
                "Signal connection '" +
                connection.id +
                "' references missing option '" +
                optionId +
                "'.",
              entityId: connection.id,
            });
          }
        }
      }
    }
  }

  const renderPlan: GraphicTree = {
    type: "group",
    id: "synth:" + document.id,
    children: artboardGraphics,
  };
  const semantics: CompiledSemantics = {
    controls: createSemanticMap(document.semantics.controls, controlBindings),
    ports: createSemanticMap(document.semantics.ports, portBindings),
    signalNodes: createSemanticMap(document.semantics.signalNodes, signalNodeBindings),
    connections: document.semantics.connections,
  };
  const hitIndex = new SpatialHitIndex(hitEntries, options.hitCellSize ?? 25);

  return {
    document,
    scene: {
      artboards: compiledArtboards,
      roots: document.roots,
      parentIndex,
    },
    semantics,
    resolvedTokens: styles.tokens,
    renderPlan,
    nodeIndex,
    hitIndex,
    portAnchors,
    signalNodeAnchors,
    diagnostics,
    valid: !diagnostics.some(({ severity }) => severity === "error"),
    contentHash: synthDocumentContentHash(document),
  };
}
